import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Phone, Mail, DollarSign, X, Trash2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';

const STAGES = [
  { key: 'presented',  label: 'Presented',  accent: '#C9A961', sub: 'Just left the consult' },
  { key: 'follow_up',  label: 'Follow-up',  accent: '#0E7490', sub: 'Awaiting decision' },
  { key: 'closed',     label: 'Closed Won', accent: '#15803D', sub: 'Sold' },
  { key: 'lost',       label: 'Closed Lost', accent: '#9F1239', sub: 'Did not move forward' },
];

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const daysAgo = (d) => {
  if (!d) return '';
  const diff = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  return diff === 0 ? 'today' : `${diff}d ago`;
};

const emptyForm = {
  location_id: '', patient_first_name: '', patient_last_name: '',
  patient_phone: '', patient_email: '', consult_date: new Date().toISOString().slice(0, 10),
  treatment_type: '', quoted_amount: 0, referral_source: '', stage: 'presented', notes: '',
};

export default function PipelinePage() {
  const [entries, setEntries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dropdowns, setDropdowns] = useState({ treatment_types: [], referral_sources: [] });
  const [locationFilter, setLocationFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(null);
  const [draggedId, setDraggedId] = useState(null);

  const fetchData = async () => {
    try {
      const [eRes, lRes, dRes] = await Promise.all([
        axios.get(`${API}/pipeline`, { params: locationFilter !== 'all' ? { location_id: locationFilter } : {} }),
        axios.get(`${API}/locations`),
        axios.get(`${API}/dropdowns`),
      ]);
      setEntries(eRes.data);
      setLocations(lRes.data);
      setDropdowns(dRes.data || { treatment_types: [], referral_sources: [] });
    } catch (err) {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [locationFilter]);

  const grouped = useMemo(() => {
    const g = { presented: [], follow_up: [], closed: [], lost: [] };
    entries.forEach((e) => { if (g[e.stage]) g[e.stage].push(e); });
    return g;
  }, [entries]);

  const getId = (e) => e.id || e._id;

  const totals = useMemo(() => {
    const t = {};
    STAGES.forEach((s) => {
      t[s.key] = grouped[s.key].reduce((acc, e) => acc + (e.quoted_amount || 0), 0);
    });
    return t;
  }, [grouped]);

  const handleCreate = async () => {
    if (!form.location_id || !form.patient_first_name || !form.patient_last_name) {
      toast.error('Location, first name, last name required');
      return;
    }
    try {
      await axios.post(`${API}/pipeline`, form);
      toast.success('Pipeline entry created');
      setShowCreate(false);
      setForm(emptyForm);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create');
    }
  };

  const moveStage = async (id, newStage) => {
    try {
      await axios.patch(`${API}/pipeline/${id}`, { stage: newStage });
      setEntries((prev) => prev.map((e) => getId(e) === id ? { ...e, stage: newStage, stage_changed_at: new Date().toISOString() } : e));
    } catch {
      toast.error('Failed to move');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pipeline entry?')) return;
    try {
      await axios.delete(`${API}/pipeline/${id}`);
      setEntries((prev) => prev.filter((e) => getId(e) !== id));
      setSelected(null);
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid="pipeline-page" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-3xl tracking-tight" style={{ color: '#0E0E0E' }}>Patient Pipeline</h2>
          <p className="text-sm text-stone-500 mt-1">Track Full Arch consults from presentation to close.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="w-[180px] h-10 bg-white" data-testid="pipeline-location-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            onClick={() => { setForm({ ...emptyForm, location_id: locations[0]?.id || '' }); setShowCreate(true); }}
            data-testid="pipeline-add-button"
            style={{ background: '#0E0E0E', color: '#C9A961' }}
            className="h-10 gap-2 uppercase tracking-wider text-xs font-semibold"
          >
            <Plus size={14} /> Add Patient
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-stone-500 text-sm">Loading pipeline…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {STAGES.map((stage) => (
            <div
              key={stage.key}
              className="rounded-lg border bg-white flex flex-col"
              style={{ borderColor: 'rgba(14,14,14,0.08)', minHeight: 500 }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData('text/plain') || draggedId;
                if (id) moveStage(id, stage.key);
                setDraggedId(null);
              }}
              data-testid={`pipeline-column-${stage.key}`}
            >
              <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'rgba(14,14,14,0.06)' }}>
                <div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: stage.accent }} />
                    <h3 className="font-heading text-base font-medium" style={{ color: '#0E0E0E' }}>{stage.label}</h3>
                    <span className="text-xs text-stone-400">{grouped[stage.key].length}</span>
                  </div>
                  <p className="text-[10px] uppercase tracking-widest mt-0.5" style={{ color: 'rgba(14,14,14,0.4)' }}>{stage.sub}</p>
                </div>
                <div className="text-xs text-stone-600 font-medium">{fmtMoney(totals[stage.key])}</div>
              </div>
              <div className="flex-1 p-3 space-y-2 overflow-y-auto">
                {grouped[stage.key].map((e) => (
                  <div
                    key={getId(e)}
                    draggable
                    onDragStart={(ev) => { setDraggedId(getId(e)); ev.dataTransfer.setData('text/plain', getId(e)); }}
                    onClick={() => setSelected(e)}
                    className="p-3 rounded-md border bg-white hover:shadow-md transition-all cursor-pointer"
                    style={{ borderColor: 'rgba(14,14,14,0.08)', borderLeft: `2px solid ${stage.accent}` }}
                    data-testid={`pipeline-card-${getId(e)}`}
                  >
                    <div className="font-medium text-sm" style={{ color: '#0E0E0E' }}>
                      {e.patient_first_name} {e.patient_last_name}
                    </div>
                    {e.treatment_type && <div className="text-xs text-stone-500 mt-0.5">{e.treatment_type}</div>}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs font-medium" style={{ color: stage.accent }}>{fmtMoney(e.quoted_amount)}</span>
                      <span className="text-[10px] text-stone-400">{daysAgo(e.stage_changed_at || e.created_at)}</span>
                    </div>
                  </div>
                ))}
                {grouped[stage.key].length === 0 && (
                  <div className="text-center py-8 text-xs text-stone-400">No patients</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="pipeline-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">New Pipeline Entry</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">First Name *</Label>
              <Input data-testid="pipeline-form-first-name" value={form.patient_first_name} onChange={(e) => setForm({ ...form, patient_first_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Last Name *</Label>
              <Input data-testid="pipeline-form-last-name" value={form.patient_last_name} onChange={(e) => setForm({ ...form, patient_last_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={form.patient_phone} onChange={(e) => setForm({ ...form, patient_phone: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.patient_email} onChange={(e) => setForm({ ...form, patient_email: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Location *</Label>
              <Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v })}>
                <SelectTrigger data-testid="pipeline-form-location"><SelectValue placeholder="Choose location" /></SelectTrigger>
                <SelectContent>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Consult Date</Label>
              <Input type="date" value={form.consult_date} onChange={(e) => setForm({ ...form, consult_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Treatment Type</Label>
              <Select value={form.treatment_type} onValueChange={(v) => setForm({ ...form, treatment_type: v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {(dropdowns.treatment_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Referral Source</Label>
              <Select value={form.referral_source} onValueChange={(v) => setForm({ ...form, referral_source: v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {(dropdowns.referral_sources || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Quoted Amount</Label>
              <Input type="number" value={form.quoted_amount} onChange={(e) => setForm({ ...form, quoted_amount: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <Label className="text-xs">Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="pipeline-create-submit" style={{ background: '#0E0E0E', color: '#C9A961' }}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Drawer */}
      {selected && (
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-lg" data-testid="pipeline-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">{selected.patient_first_name} {selected.patient_last_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {selected.patient_phone && <div className="flex items-center gap-2 text-sm"><Phone size={14} /> {selected.patient_phone}</div>}
              {selected.patient_email && <div className="flex items-center gap-2 text-sm"><Mail size={14} /> {selected.patient_email}</div>}
              {selected.treatment_type && <div className="text-sm"><span className="text-stone-500">Treatment:</span> {selected.treatment_type}</div>}
              <div className="flex items-center gap-2 text-sm"><DollarSign size={14} /> {fmtMoney(selected.quoted_amount)}</div>
              {selected.referral_source && <div className="text-sm"><span className="text-stone-500">Source:</span> {selected.referral_source}</div>}
              {selected.notes && <div className="text-sm text-stone-700 bg-stone-50 p-2 rounded border">{selected.notes}</div>}

              <div className="pt-2">
                <Label className="text-xs">Move to stage</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {STAGES.filter((s) => s.key !== selected.stage).map((s) => (
                    <button
                      key={s.key}
                      onClick={() => { moveStage(getId(selected), s.key); setSelected({ ...selected, stage: s.key }); }}
                      className="text-xs px-3 py-1.5 rounded border hover:bg-stone-50 inline-flex items-center gap-1"
                      data-testid={`pipeline-move-${s.key}`}
                      style={{ borderColor: s.accent, color: s.accent }}
                    >
                      <ArrowRight size={11} /> {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <button
                onClick={() => handleDelete(getId(selected))}
                className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                data-testid="pipeline-delete-button"
              >
                <Trash2 size={12} /> Delete entry
              </button>
              <Button variant="outline" onClick={() => setSelected(null)}><X size={14} /> Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
